import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CyberTopology,
  CyberTopologyDraft,
  CyberTopologyShare,
  CyberTopologySummary,
} from '../models/cyber-topology.model';
import { environment } from '../../environments/environment';

/**
 * CRUD for user-authored network topologies.
 *
 * Deliberately uncached, unlike {@link CyberSiteService}: topologies are edited in this very
 * page, so a cached list would go stale the moment the user saved.
 */
@Injectable({ providedIn: 'root' })
export class CyberTopologyService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiBaseUrl}/cyber/topologies`;

  /** Everything the user may read — their own, plus what has been shared with them. */
  list(): Observable<CyberTopologySummary[]> {
    return this.http.get<CyberTopologySummary[]>(this.apiUrl);
  }

  get(id: number): Observable<CyberTopology> {
    return this.http.get<CyberTopology>(`${this.apiUrl}/${id}`);
  }

  create(draft: CyberTopologyDraft): Observable<CyberTopology> {
    return this.http.post<CyberTopology>(this.apiUrl, draft);
  }

  update(id: number, draft: CyberTopologyDraft): Observable<CyberTopology> {
    return this.http.put<CyberTopology>(`${this.apiUrl}/${id}`, draft);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  listShares(id: number): Observable<CyberTopologyShare[]> {
    return this.http.get<CyberTopologyShare[]>(`${this.apiUrl}/${id}/shares`);
  }

  addShare(
    id: number,
    subjectType: 'USER' | 'ORGANIZATION',
    subjectId: string,
  ): Observable<CyberTopologyShare> {
    return this.http.post<CyberTopologyShare>(`${this.apiUrl}/${id}/shares`, {
      subjectType,
      subjectId,
    });
  }

  removeShare(id: number, shareId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/shares/${shareId}`);
  }
}
